import { io, Socket } from 'socket.io-client';
import { Notification } from '../types/notification';

class WebSocketService {
  private socket: Socket | null = null;
  private listeners: { [event: string]: ((data: any) => void)[] } = {};
  private isConnecting = false;
  private connectionPromise: Promise<void> | null = null;

  async connect(userId?: number): Promise<void> {
    // If already connected, return
    if (this.socket?.connected) {
      console.log('WebSocket already connected');
      return;
    }

    // If currently connecting, wait for the connection
    if (this.isConnecting && this.connectionPromise) {
      console.log('WebSocket connection in progress, waiting...');
      return this.connectionPromise;
    }

    this.isConnecting = true;
    
    this.connectionPromise = new Promise((resolve, reject) => {
      const apiBaseURL = import.meta.env.VITE_API_BASEURL || 'http://localhost:3000/rmsadmin/api/v1';
      
      // For production VPS deployment
      let socketURL: string;
      let socketPath = '/socket.io/';
      
      if (import.meta.env.VITE_WEBSOCKET_URL) {
        // Use explicit WebSocket URL if provided
        socketURL = import.meta.env.VITE_WEBSOCKET_URL;
      } else {
        // Extract the base domain from the API URL for WebSocket connection
        const urlParts = apiBaseURL.match(/^(https?:\/\/[^\/]+)/);
        const baseURL = urlParts ? urlParts[1] : 'http://localhost:3000';
        
        // Handle subdomain deployments (e.g., admin.yourdomain.com)
        socketURL = baseURL;
      }
      
      // Use custom socket path if provided (for proxy configurations)
      if (import.meta.env.VITE_WEBSOCKET_PATH) {
        socketPath = import.meta.env.VITE_WEBSOCKET_PATH;
      }
      
      console.log('Connecting to WebSocket at:', socketURL, 'with path:', socketPath);
      
      this.socket = io(socketURL, {
        withCredentials: true,
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        path: socketPath,
        // Add timeout for slower connections
        timeout: 20000,
        // Force new connection
        forceNew: true,
      });

      const connectionTimeout = setTimeout(() => {
        console.error('WebSocket connection timeout');
        this.isConnecting = false;
        reject(new Error('Connection timeout'));
      }, 30000); // 30 second timeout for VPS connections

      this.socket.on('connect', () => {
        clearTimeout(connectionTimeout);
        console.log('WebSocket connected successfully');
        this.isConnecting = false;
        
        // Register user after connection
        if (userId) {
          console.log('Registering user:', userId);
          this.socket?.emit('register', { userId });
        }
        
        resolve();
      });

      this.socket.on('disconnect', (reason) => {
        console.log('WebSocket disconnected:', reason);
        this.isConnecting = false;
      });

      this.socket.on('connect_error', (error) => {
        console.error('WebSocket connection error:', error.message);
        clearTimeout(connectionTimeout);
        this.isConnecting = false;
        
        // Don't reject on connection errors in production, just log
        if (import.meta.env.PROD) {
          console.warn('WebSocket connection failed, app will continue without real-time updates');
          resolve();
        } else {
          reject(error);
        }
      });

      this.socket.on('error', (error) => {
        console.error('WebSocket error:', error);
      });

      // Re-emit events to registered listeners
      this.socket.on('notification', (data: Notification) => {
        console.log('Received notification:', data);
        this.emit('notification', data);
      });

      this.socket.on('notification:read', (data: { notificationIds: number[] }) => {
        console.log('Received notification:read:', data);
        this.emit('notification:read', data);
      });

      this.socket.on('notification:archived', (data: { notificationIds: number[] }) => {
        console.log('Received notification:archived:', data);
        this.emit('notification:archived', data);
      });

      this.socket.on('notification:deleted', (data: { notificationId: number }) => {
        console.log('Received notification:deleted:', data);
        this.emit('notification:deleted', data);
      });
    });

    return this.connectionPromise;
  }

  disconnect() {
    if (this.socket && this.socket.connected) {
      console.log('Disconnecting WebSocket');
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
      this.isConnecting = false;
      this.connectionPromise = null;
    }
  }

  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  on(event: string, callback: (data: any) => void) {
    if (!this.listeners[event]) {
      this.listeners[event] = [];
    }
    this.listeners[event].push(callback);
  }

  off(event: string, callback: (data: any) => void) {
    if (this.listeners[event]) {
      this.listeners[event] = this.listeners[event].filter(cb => cb !== callback);
    }
  }

  private emit(event: string, data: any) {
    if (this.listeners[event]) {
      this.listeners[event].forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in listener for event ${event}:`, error);
        }
      });
    }
  }

  // Send events to server
  sendNotification(notification: Partial<Notification>) {
    if (this.socket?.connected) {
      this.socket.emit('notification:create', notification);
    } else {
      console.warn('Cannot send notification: WebSocket not connected');
    }
  }

  markAsRead(notificationIds: number[]) {
    if (this.socket?.connected) {
      this.socket.emit('notification:mark-read', { notificationIds });
    } else {
      console.warn('Cannot mark as read: WebSocket not connected');
    }
  }

  archive(notificationIds: number[]) {
    if (this.socket?.connected) {
      this.socket.emit('notification:archive', { notificationIds });
    } else {
      console.warn('Cannot archive: WebSocket not connected');
    }
  }

  delete(notificationId: number) {
    if (this.socket?.connected) {
      this.socket.emit('notification:delete', { notificationId });
    } else {
      console.warn('Cannot delete: WebSocket not connected');
    }
  }
}

export const websocketService = new WebSocketService();